import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useChat } from '../hooks/useChat';

// Define the task type
export interface Task {
  id: string;
  summary: string;
  details: string;
  category: string;
  area_name: string;
  is_completed: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

interface TasksContextProps {
  tasks: Task[];
  isLoading: boolean;
  completedTaskIds: Set<string>;
  toggleTaskCompletion: (taskId: string) => void;
  updateTask: (taskId: string, areaName: string, taskSummary: string, updates: TaskUpdates) => Promise<boolean>;
  toggleTaskCompletionWithServer: (taskId: string, areaName: string, taskSummary: string) => Promise<boolean>;
  isUpdatingTask: boolean;
  taskUpdateError: string | null;
  clearTaskUpdateError: () => void;
  getActiveTaskCount: () => number;
  refreshTasks: () => Promise<void>;
}

interface TasksProviderProps {
  children: ReactNode;
  homeId: string;
}

interface TaskUpdates {
  is_completed?: boolean;
  summary?: string;
  details?: string;
  category?: string;
  notes?: string;
}

export const TasksContext = createContext<TasksContextProps | undefined>(undefined);

export function TasksProvider({ children, homeId }: TasksProviderProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(new Set());
  const [isUpdatingTask, setIsUpdatingTask] = useState<boolean>(false);
  const [taskUpdateError, setTaskUpdateError] = useState<string | null>(null);
  
  // Get the lastUpdated from the chat context to know when to refresh
  const { lastUpdated } = useChat();

  // Function to toggle task completion locally (client-side only)
  const toggleTaskCompletion = useCallback((taskId: string) => {
    setCompletedTaskIds(prevCompletedTasks => {
      const newCompletedTasks = new Set(prevCompletedTasks);
      if (newCompletedTasks.has(taskId)) {
        newCompletedTasks.delete(taskId);
      } else {
        newCompletedTasks.add(taskId);
      }
      return newCompletedTasks;
    });
  }, []);

  // Function to fetch tasks from the API - MOVED BEFORE updateTask
  const refreshTasks = useCallback(async () => {
    if (!homeId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`/api/homes/${homeId}/tasks`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success && Array.isArray(data.tasks)) {
        // Use function update to access current tasks state without dependency
        setTasks(currentTasks => {
          // Create a map of existing tasks by ID for quick lookups
          const existingTasksMap = new Map(
            currentTasks.map(task => [task.id, task])
          );
          
          // Create new tasks array that preserves references when possible
          const updatedTasks = data.tasks.map((newTask: Task) => {
            const existingTask = existingTasksMap.get(newTask.id);
            
            if (existingTask) {
              // If task exists, merge properties but maintain reference
              return { ...existingTask, ...newTask };
            }
            
            // If it's a new task, add it as is
            return newTask;
          });
          
          return updatedTasks;
        });
        
        // Update completed tasks set
        setCompletedTaskIds(() => {
          const completedIds = new Set<string>();
          data.tasks.forEach((task: Task) => {
            if (task.is_completed) {
              completedIds.add(task.id);
            }
          });
          return completedIds;
        });
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsLoading(false);
    }
  }, [homeId]); // Removed 'tasks' from dependency array

  // Function to update task on the server - NOW AFTER refreshTasks
  const updateTask = useCallback(async (
    taskId: string, 
    areaName: string, 
    taskSummary: string, 
    updates: TaskUpdates
  ) => {
    if (!homeId) {
      setTaskUpdateError('No home ID available');
      return false;
    }

    // Clear any previous errors
    setTaskUpdateError(null);
    setIsUpdatingTask(true);

    try {
      const response = await fetch(`/api/homes/${homeId}/tasks`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          taskId,
          areaName,
          taskSummary,
          updates
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to update task');
      }

      // If we're updating completion status, we've already updated the UI state
      // in toggleTaskCompletionWithServer, so no need to do it again here
      
      // Only refresh tasks for other types of updates (summary, details, etc.)
      if (updates.is_completed === undefined) {
        await refreshTasks();
      }
      
      return true;
    } catch (error) {
      console.error('Error updating task:', error);
      setTaskUpdateError(error instanceof Error ? error.message : 'Failed to update task');
      return false;
    } finally {
      setIsUpdatingTask(false);
    }
  }, [homeId, refreshTasks]);

  // Convenience function for toggling completion status with server
  const toggleTaskCompletionWithServer = useCallback(async (
    taskId: string,
    areaName: string,
    taskSummary: string
  ) => {
    const isCurrentlyCompleted = completedTaskIds.has(taskId);
    const newCompletionStatus = !isCurrentlyCompleted;
    
    // Optimistically update UI
    toggleTaskCompletion(taskId);
    
    // Update server
    const success = await updateTask(
      taskId,
      areaName,
      taskSummary,
      { is_completed: newCompletionStatus }
    );
    
    // If server update failed, revert the UI change
    if (!success) {
      toggleTaskCompletion(taskId);
    }
    
    return success;
  }, [completedTaskIds, toggleTaskCompletion, updateTask]);

  // Function to get count of active (uncompleted) tasks
  const getActiveTaskCount = useCallback(() => {
    return tasks.length - completedTaskIds.size;
  }, [tasks, completedTaskIds]);

  // Clear task update error
  const clearTaskUpdateError = useCallback(() => {
    setTaskUpdateError(null);
  }, []);

  // Fetch tasks on initial load
  useEffect(() => {
    if (homeId) {
      refreshTasks();
    }
  }, [homeId, refreshTasks]);

  // Refresh tasks when lastUpdated changes in the chat context
  useEffect(() => {
    if (lastUpdated && homeId) {
      refreshTasks();
    }
  }, [lastUpdated, homeId, refreshTasks]);

  return (
    <TasksContext.Provider value={{
      tasks,
      isLoading,
      completedTaskIds,
      toggleTaskCompletion,
      updateTask,
      toggleTaskCompletionWithServer,
      isUpdatingTask,
      taskUpdateError,
      clearTaskUpdateError,
      getActiveTaskCount,
      refreshTasks
    }}>
      {children}
    </TasksContext.Provider>
  );
}

export const useTasks = () => {
  const context = useContext(TasksContext);
  if (context === undefined) {
    throw new Error('useTasks must be used within a TasksProvider');
  }
  return context;
};